import { Fragment, useState, useMemo } from "react";
import { UNDERSTANDING_DOCS } from "../docs/fiDocs";
import ModalModern from "../../../../../../components/ui/popup/ModalModern";
import ModernPopupHeader from "../../../../../../components/ui/popup/common/ModernPopupHeader";
import SegmentMenu from "../../../../../../components/navegation/SegmentMenu";
import ModernPopupBody from "../../../../../../components/ui/popup/common/ModernPopupBody";
import FAQPreview from "../../../../../../components/common/FAQPreview";
import RenderContentBlocks from "../docs/renders/RenderContentBlocks";
import Breadcrumb from "../../../../../../components/ui/Breadcrumb";

function FiUnderstandingModal({ functionalityKey, onClose }) {
  const docConfig = UNDERSTANDING_DOCS[functionalityKey];

  const [activeTab, setActiveTab] = useState(
    docConfig?.navigation[0]?.path || "",
  );

  const [selectedArticle, setSelectedArticle] = useState(null);

  const menuSegments = useMemo(() => {
    if (!docConfig) return [];
    return docConfig.navigation.map((nav) => ({
      ...nav,
      onClick: (e) => {
        e.preventDefault();
        setActiveTab(nav.path);
        setSelectedArticle(null);
      },
    }));
  }, [docConfig]);

  const breadcrumbItems = useMemo(() => {
    if (!docConfig || !selectedArticle) return [];

    // Find the text label of your active tab (e.g., "Help Center")
    const activeTabLabel =
      docConfig.navigation.find((n) => n.path === activeTab)?.text || "";

    return [
      {
        label: activeTabLabel,
        to: "#", // Keeps visual link formatting styles active
        onClick: (e) => {
          e.preventDefault();
          setSelectedArticle(null); // Back to article list index pane
        },
      },
      {
        label: selectedArticle.title, // Current active article name
      },
    ];
  }, [docConfig, activeTab, selectedArticle]);

  if (!docConfig) return null;

  const currentTabMeta = docConfig.meta?.[activeTab] || { layoutType: "list" };
  const articlesList = docConfig.content[activeTab] || [];

  return (
    <Fragment>
      <ModalModern onClose={onClose}>
        {/* MODAL HEADER BLOCK */}
        <ModernPopupHeader
          onClose={onClose}
          segmentPaddingButton={!selectedArticle}
        >
          <div className="header-content-modern-col">
            {selectedArticle ? (
              /* --- VIEW B HEADER: DYNAMIC BREADCRUMB TRAIL --- */
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <h3 className="transaction-popup-title">{docConfig.title}</h3>
                <Breadcrumb items={breadcrumbItems} />
              </div>
            ) : (
              /* --- VIEW A HEADER: SHOW TITLE --- */
              <h3 className="transaction-popup-title">{docConfig.title}</h3>
            )}

            {/* --- CONDITIONALLY RENDER SEGMENTS --- */}
            {!selectedArticle && (
              <SegmentMenu
                isStatic={true}
                segments={menuSegments}
                currentPath={activeTab}
              />
            )}
          </div>
        </ModernPopupHeader>

        {/* MODAL BODY BLOCK */}
        <ModernPopupBody>
          <div className="mp-col-container">
            {selectedArticle ? (
              /* --- VIEW B: ARTICLE DETAIL DRILL-DOWN --- */
              <div
                className="article-detail-view"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {/* Clean invocation mapping right through our new parsing wrapper */}
                  <RenderContentBlocks blocks={selectedArticle.bodyContent} />
                </div>
              </div>
            ) : currentTabMeta.layoutType === "accordion" ? (
              /* --- VIEW C: ACCORDION FORMAT (FAQ PREVIEW) --- */
              <div className="accordion-layout-view">
                <FAQPreview
                  faqs={articlesList}
                  more={false}
                  maxWidthCard={true}
                />
              </div>
            ) : (
              /* --- VIEW A: ARTICLES LIST INDEX --- */
              <div className="articles-list-view">
                {articlesList.length === 0 ? (
                  <p
                    style={{
                      color: "#71767A",
                      textAlign: "center",
                      marginTop: "4px",
                    }}
                  >
                    No articles available under this section.
                  </p>
                ) : (
                  articlesList.map((article) => (
                    <div
                      key={article.id}
                      onClick={() => setSelectedArticle(article)}
                      style={{
                        display: "flex",
                        padding: "8px 12px",
                        width: "100%",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/images/icons/news_grey.svg`}
                          alt="Icon"
                          className="sidebar-icon"
                        />
                        <h3
                          style={{
                            color: "#666b6f",
                            fontSize: "14px",
                            fontStyle: "normal",
                            fontWeight: "400",
                          }}
                        >
                          {article.title}
                        </h3>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </ModernPopupBody>
      </ModalModern>
    </Fragment>
  );
}

export default FiUnderstandingModal;
